import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { numericFormatter } from 'react-number-format';

import Autocomplete from '@material-ui/lab/Autocomplete';
import { Grid, Typography, TextField, Tooltip, FormControlLabel, Radio, Button as MUIButton } from '@material-ui/core';
import ModalDrawerSwitcher from 'components/ModalDrawerSwitcher';
import Loader from 'components/Loader';
import NumericInput from 'components/NumericInput';
import Button from 'components/Button';

import { clearComissionsErrors } from '../store/actions';
import { loadComissionConfigList, upsertComissionConfig } from '../store/thunk';
import { loadServicesCategories } from 'pages/Services/store/thunk';
import { loadFilters } from 'components/Filter/store/thunk';

import useWindowDimensions from 'helpers/hooks/useWindowDimensions';
import { formatServicesCategories } from 'helpers/formatters';

import { useStyles } from './styles';

export default function Comissions({ open, onClose }) {
  const dispatch = useDispatch();

  const autoCompleteStyles = useStyles();

  const { width } = useWindowDimensions();
  const isMobile = width < 960;

  const allEstablishments = useSelector(state => state.login.establishments);
  const { userId } = useSelector(state => state.profile);
  const { establishmentId, establishments } = useSelector(state => state.businessInfo);
  const { submit, isLoading } = useSelector(state => state.comissions);
  const { selects } = useSelector(state => state.filters);
  const { servicesCategories } = useSelector(state => state.services);

  const establishmentUuid = establishments?.find(establishment => +establishment?.establishmentId == +establishmentId)?.uuid || '';
  const alternativeEstablishmentUuid = allEstablishments?.find(establishment => +establishment?.establishmentId == +establishmentId)?.uuid || '';

  const services = selects
    ?.find(service => service?.id == 'services')?.options
    ?.map(service => {
      return {
        label: service?.description,
        value: service?.serviceId,
        categories: service?.categories?.filter(category => !!category)
      }
    })
    ?.filter(service => !!service?.value);

  const commissionProfileOptions = useSelector(state => state.comissionedList.filters.commissionProfiles)?.options;

  const profiles = useMemo(() => {
    return commissionProfileOptions
      ?.map(profile => {
        return {
          label: profile?.name,
          value: profile?.id
        }
      })
      ?.filter(profile => profile?.value != '');
  }, [commissionProfileOptions]);

  const [selectedServiceId, setSelectedServiceId] = useState();

  const [servicesList, setServicesList] = useState({
    establishmentUuid,
    uuid: '',
    serviceId: selectedServiceId,
    profiles: []
  });

  const requestLoadComissionConfigList = (establishmentId, serviceId) => {
    dispatch(loadComissionConfigList({
      userId,
      establishmentId,
      serviceId
    }));
  }

  useEffect(() => {
    dispatch(clearComissionsErrors());
  }, []);

  useEffect(() => {
    if(establishmentId) {
      dispatch(loadServicesCategories(userId));
      dispatch(loadFilters(userId, establishmentId));
    }
  }, [establishmentId]);

  useEffect(() => {
    if(services?.length > 0) {
      setSelectedServiceId(services[0]?.value);
      requestLoadComissionConfigList(establishmentId, services[0]?.value);
    }
  }, []);

  const submitSignature = JSON.stringify({
    uuid: submit?.uuid || '',
    serviceId: submit?.serviceId || '',
    establishmentUuid: submit?.establishmentUuid || '',
    profiles: submit?.profiles || []
  });

  useEffect(() => {
    const savedProfiles = Array.isArray(submit?.profiles) ? submit.profiles : [];

    const profilesToServicesList = (profiles || []).map((profile, index) => {
      const savedById = savedProfiles.find(item => item?.profileId != null && String(item.profileId) === String(profile?.value));
      const saved = savedById || savedProfiles[index] || {};

      return {
        profileId: profile?.value,
        type: saved?.type ?? '1',
        amount: saved?.amount ?? ''
      };
    });

    setServicesList({
      establishmentUuid: submit?.establishmentUuid || establishmentUuid,
      uuid: submit?.uuid || '',
      serviceId: submit?.serviceId || selectedServiceId,
      profiles: profilesToServicesList
    });
  }, [submitSignature, profiles]);

  const handleServiceListChange = (profileId, params) => {
    setServicesList(current => ({
      ...current,
      profiles: current.profiles?.map(profile => {
        return String(profile?.profileId) === String(profileId) ? {
          ...profile,
          ...params
        } : {
          ...profile
        }
      })
    }));
  }

  const handleSave = () => {
    const profiles = servicesList.profiles;

    const newProfiles = profiles
      ?.map(profile => {
        delete profile?.label;
        return profile;
      })
      ?.map(profile => {
        return {
          ...profile,
          profileId: profile?.profileId,
          type: +profile?.type,
          amount: +profile.type == 1 ? profile?.amount?.toString()?.split(',')[0] : profile?.amount
        }
      });

    const submitServicesList = {
      ...servicesList,
      establishmentUuid: !!establishmentUuid ? establishmentUuid : alternativeEstablishmentUuid,
      serviceId: selectedServiceId,
      profiles: newProfiles?.map(profile => {
        return {
          ...profile,
          amount: !profile?.amount ? 0 : profile?.amount
        }
      })
    }

    dispatch(upsertComissionConfig(userId, establishmentId, submitServicesList))
      .then(() => onClose());
  }

  return(
    <ModalDrawerSwitcher
      id='comissions-config-drawer'
      title='Configurações do comissionamento'
      open={open}
      onClose={onClose}
    >
      <Grid xs={12} alignItems='center' style={{ height: '100%' }}>
        <Loader isLoading={isLoading}>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
            <div>
              <Grid container spacing={2} style={{ padding: '0 15px 0 15px' }}>
                <Grid item xs={12} spacing={2}>
                  <Autocomplete
                    id="serviceOptions"
                    getOptionLabel={option => option.label}
                    classes={autoCompleteStyles}
                    options={services}
                    loading={false}
                    value={services?.find(service => +service?.value == +selectedServiceId)}
                    loadingText={<p>Carregando...</p>}
                    noOptionsText={<p>Sem opções</p>}
                    onChange={(_, service) => {
                      if(service) {
                        setSelectedServiceId(service.value);
                        requestLoadComissionConfigList(establishmentId, service.value);
                      }
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Serviço"
                        variant="outlined"
                      />
                    )}
                    renderOption={props => {
                      return(
                        <span {...props}>
                          <Typography color='primary'>{props.label}</Typography>
                          {(props?.categories?.length > 0) && (
                            <Typography color='textSecondary'>Categoria(s): {formatServicesCategories(props?.categories, servicesCategories)}</Typography>
                          )}
                        </span>
                      );
                    }}
                  />
                </Grid>
              </Grid>
              {servicesList?.profiles?.map(profile => (
                <>
                  <Grid container spacing={2} style={{ borderBottom: '1px solid #E4E7EA', marginBottom: 10 }}>
                    <Grid item xs={6}>
                      <Typography variant='subtitle2' color='primary'>
                        {profiles?.find(profileList => +profileList?.value == +profile?.profileId)?.label}
                      </Typography>
                    </Grid>
                    {isMobile && (
                      <Grid item xs={6}>
                        <Typography variant='subtitle2' color='primary'>
                          Tarifa de comissão
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={6} spacing={2} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <NumericInput
                        name={profile?.profileId}
                        type='text'
                        label='Valor'
                        hasMaxValue
                        maxValue={+profile.type == 1 ? 99.99 : 999999.99}
                        value={+profile.type == 1 ? profile?.amount : numericFormatter(profile?.amount?.toString() || '', { thousandSeparator: '.', decimalSeparator: ',', decimalScale: 2, fixedDecimalScale: 2 })}
                        onChange={value => handleServiceListChange(profile?.profileId, { amount: value })}
                      />
                    </Grid>
                    <Grid item xs={6} spacing={2} style={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', alignItems: 'center', justifyContent: 'center' }}>
                      {isMobile && (
                        <Grid container>
                          <Grid item xs={12} spacing={1} style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                            <MUIButton
                              style={{ height: 40, borderTopRightRadius: 0, borderBottomRightRadius: 0, color: +profile?.type == 2 ? '#FFFFFF' : '#757575' }}
                              color={+profile?.type == 2 ? 'primary' : 'tertiary'}
                              variant={+profile?.type == 2 ? 'contained' : 'outlined'}
                              onClick={() => handleServiceListChange(profile?.profileId, { amount: '', type: '2' })}
                            >
                              R$
                            </MUIButton>
                            <MUIButton
                              style={{ height: 40, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, color: +profile?.type == 1 ? '#FFFFFF' : '#757575' }}
                              color={+profile?.type == 1 ? 'primary' : 'tertiary'}
                              variant={+profile?.type == 1 ? 'contained' : 'outlined'}
                              onClick={() => handleServiceListChange(profile?.profileId, { amount: '', type: '1' })}
                            >
                              %
                            </MUIButton>
                          </Grid>
                        </Grid>
                      )}
                      {!isMobile && (
                        <div style={{ display: 'flex', alignItems: 'flex-start', width: isMobile ? '100%' : '60%', marginTop: 2.5 }}>
                          <FormControlLabel
                            id='percentage-radio'
                            name='percentageRadio'
                            label={
                              <Tooltip
                                title='Valor percentual'
                                placement='top'
                                enterTouchDelay={0}
                              >
                                <Typography color='textSecondary'>
                                  Percentual (%)
                                </Typography>
                              </Tooltip>
                            }
                            control={
                              <Radio
                                style={{ padding: 2.5 }}
                                color='primary'
                                checked={+profile?.type == 1}
                                onClick={() => handleServiceListChange(profile?.profileId, { amount: '', type: '1' })}
                              />
                            }
                          />
                          <FormControlLabel
                            id='monetary-radio'
                            name='monetaryRadio'
                            label={
                              <Tooltip
                                title='Valor absoluto em real'
                                placement='top'
                                enterTouchDelay={0}
                              >
                                <Typography color='textSecondary'>
                                  Monetário ($)
                                </Typography>
                              </Tooltip>
                            }
                            control={
                              <Radio
                                style={{ padding: 2.5 }}
                                color='primary'
                                checked={+profile?.type == 2}
                                onClick={() => handleServiceListChange(profile?.profileId, { amount: '', type: '2' })}
                              />
                            }
                          />
                        </div>
                      )}
                    </Grid>
                  </Grid>
                </>
              ))}
            </div>
            <div>
              <Grid container spacing={2} style={{ padding: '0 15px 10px 15px' }}>
                <Grid item xs={12} spacing={2} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type='submit'
                    color='success'
                    onClick={handleSave}
                  >
                    Salvar
                  </Button>
                </Grid>
              </Grid>
            </div>
          </div>
        </Loader>
      </Grid>
    </ModalDrawerSwitcher>
  );
}